import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import {
  Download,
  CheckCircle,
  Usb,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from './PublicNavBar';
import PublicFooter from './PublicFooter';
import { trackGatewayInstall } from '../analytics';

const DISTRO_ZIP_URL = 'https://eyesense-downloads-435236352827.s3.us-east-1.amazonaws.com/eyesense-ai-distro.zip';
const USB_ISO_URL = 'https://eyesense-downloads-435236352827.s3.us-east-1.amazonaws.com/eyesense-usb-installer.iso';
const USB_ISO_SHA_URL = `${USB_ISO_URL}.sha256`;
const USB_CARD_URL = 'https://eyesense-downloads-435236352827.s3.us-east-1.amazonaws.com/eyesense-usb-installer.txt';
const USB_FACTORY_USER = 'eyesense';
const USB_FACTORY_PASSWORD = 'gDH9xR1Uu7wbvT';

// Core content component - reusable for both standalone and with-nav variants
const GatewayInstallationContent = ({ showNavBar = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [installChoice, setInstallChoice] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [burnHost, setBurnHost] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (installChoice) {
      trackGatewayInstall(installChoice);
    }
  }, [installChoice]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const deploymentSteps = [
    {
      title: 'Verify System Requirements',
      description: 'Ensure your system meets minimum requirements',
      commands: [
        { label: 'Check Docker installation:', cmd: 'docker --version && docker-compose --version' },
        { label: 'Check available disk space:', cmd: 'df -h' },
        { label: 'Check RAM:', cmd: 'free -h' }
      ]
    },
    {
      title: 'Download, Extract & Deploy the Package',
      description: 'Download, unzip and run the setup script - everything happens on your device',
      content: (
        <Box>
          {platform === 'windows' && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>Windows</Typography>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <strong>First-time only:</strong> Windows requires WSL2 (Windows Subsystem for Linux) to run the setup script
                </Alert>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                  Step 1: Install WSL2 (One-time setup)
                </Typography>
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2">
                    1. Open <strong>PowerShell as Administrator</strong> (right-click PowerShell, select "Run as administrator")<br/>
                    2. Paste this command and press Enter:
                  </Typography>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 3 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>wsl --install</pre>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Restart your computer. WSL2 will finish installing automatically.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                  Step 2: Open WSL Terminal
                </Typography>
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2">
                    1. Open <strong>PowerShell</strong> or <strong>Command Prompt</strong><br/>
                    2. Type: <code>wsl</code> and press <strong>Enter</strong><br/>
                    A WSL terminal will open
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                  Step 3: Download, Extract & Deploy
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>curl -L -o eyesense-ai-distro.zip {DISTRO_ZIP_URL}</pre>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                  This downloads the package directly from eyesense.ai to your WSL home directory.
                </Typography>

                <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 3 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>unzip eyesense-ai-distro.zip<br/>cd distro<br/>./setup.sh</pre>
                </Box>
                <Alert severity="success">
                  The setup script handles Docker, WireGuard, database, and hub registration automatically.
                </Alert>
              </Box>
          )}

          {platform === 'macos' && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>Mac</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                  Step 1: Open Terminal
                </Typography>
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2">
                    1. Press <strong>Cmd + Space</strong> (Spotlight Search)<br/>
                    2. Type: <code>Terminal</code><br/>
                    3. Press <strong>Enter</strong><br/>
                    A terminal window will open
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                  Step 2: Download, Extract & Deploy
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>curl -L -o eyesense-ai-distro.zip {DISTRO_ZIP_URL}</pre>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                  This downloads the package directly from eyesense.ai to your home directory (~/).
                </Typography>

                <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 3 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>unzip eyesense-ai-distro.zip<br/>cd distro<br/>./setup.sh</pre>
                </Box>
                <Alert severity="success">
                  The setup script handles Docker, WireGuard, database, and hub registration automatically.
                </Alert>
              </Box>
          )}

          {platform === 'linux' && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>Linux</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                  Step 1: Open Terminal
                </Typography>
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2">
                    Press <strong>Ctrl + Alt + T</strong><br/>
                    A terminal window will open
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                  Step 2: Download, Extract & Deploy
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>curl -L -o eyesense-ai-distro.zip {DISTRO_ZIP_URL}</pre>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                  This downloads the package directly from eyesense.ai to your home directory (~/).
                </Typography>

                <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 3 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>unzip eyesense-ai-distro.zip<br/>cd distro<br/>./setup.sh</pre>
                </Box>
                <Alert severity="success">
                  The setup script handles Docker, WireGuard, database, and hub registration automatically.
                </Alert>
              </Box>
          )}
        </Box>
      )
    },
    {
      title: 'Approve Gateway & Complete Setup',
      description: 'The setup.sh script handles everything automatically',
      content: (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>The setup.sh script automatically:</strong>
          </Alert>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: theme.palette.success.main }} /></ListItemIcon>
              <ListItemText primary="✅ Checks Docker installation and starts it if needed" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: theme.palette.success.main }} /></ListItemIcon>
              <ListItemText primary="✅ Sets up WireGuard VPN to connect to EyeSense.ai hub" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: theme.palette.success.main }} /></ListItemIcon>
              <ListItemText primary="✅ Initializes the local database (Liquibase migrations)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: theme.palette.success.main }} /></ListItemIcon>
              <ListItemText primary="✅ Starts all services in Docker containers" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: theme.palette.success.main }} /></ListItemIcon>
              <ListItemText primary="✅ Automatically registers with the central EyeSense.ai hub" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: theme.palette.success.main }} /></ListItemIcon>
              <ListItemText primary="✅ Displays your gateway's unique device token" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: theme.palette.success.main }} /></ListItemIcon>
              <ListItemText primary="✅ Enables the nightly auto-update timer (S3 zip + sidecar)" />
            </ListItem>
          </List>
          <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${theme.palette.info.main}` }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>✅ Complete!</strong> Your gateway is automatically approved and connected to the central hub.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Your gateway is now running locally and securely connected to the central hub via WireGuard VPN. All AI processing happens locally on your device. Your device token is displayed above for reference.
            </Typography>
          </Box>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4, pt: showNavBar ? 10 : 2 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Install EyeSense
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', mb: 4 }}>
          Do you want to wipe a new computer, or install the software on a computer you already use?
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Card
              onClick={() => {
                setInstallChoice('wipe');
                setPlatform(null);
                setBurnHost(null);
              }}
              sx={{
                height: '100%',
                cursor: 'pointer',
                textAlign: 'left',
                border: '2px solid',
                borderColor: installChoice === 'wipe' ? 'warning.main' : 'divider',
              }}
            >
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Wipe a new computer
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Best option for installers selling gateway hardware. Erase the hard drive and install Ubuntu 24.04 with EyeSense already on it.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              onClick={() => {
                setInstallChoice('package');
                setPlatform(null);
                setBurnHost(null);
              }}
              sx={{
                height: '100%',
                cursor: 'pointer',
                textAlign: 'left',
                border: '2px solid',
                borderColor: installChoice === 'package' ? 'primary.main' : 'divider',
              }}
            >
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Use the installer package
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Keep the operating system you already have, and install the EyeSense software on it.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {installChoice === 'wipe' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Which computer are you using to copy the installer onto the USB stick?
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {[
                { id: 'windows', title: 'Windows', detail: 'Flash the ISO with balenaEtcher.' },
                { id: 'macos', title: 'Mac', detail: 'Flash with balenaEtcher, or from Terminal.' },
                { id: 'linux', title: 'Linux', detail: 'Flash the ISO with dd.' },
              ].map((item) => (
                <Grid item xs={12} md={4} key={item.id}>
                  <Card
                    onClick={() => {
                      setBurnHost(item.id);
                      window.setTimeout(() => document.getElementById('install-steps')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                    }}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      textAlign: 'left',
                      border: '2px solid',
                      borderColor: burnHost === item.id ? 'warning.main' : 'divider',
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.detail}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {installChoice === 'package' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Which operating system is already on the computer?
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {[
                { id: 'windows', title: 'Windows', detail: 'Installs WSL2, then the EyeSense package.' },
                { id: 'macos', title: 'Mac', detail: 'Uses Terminal on macOS.' },
                { id: 'linux', title: 'Linux', detail: 'Uses the terminal on Ubuntu, Debian, and other Linux systems.' },
              ].map((item) => (
                <Grid item xs={12} md={4} key={item.id}>
                  <Card
                    onClick={() => {
                      setPlatform(item.id);
                      window.setTimeout(() => document.getElementById('install-steps')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                    }}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      textAlign: 'left',
                      border: '2px solid',
                      borderColor: platform === item.id ? 'primary.main' : 'divider',
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.detail}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {installChoice && (
          <Button sx={{ mt: 2 }} onClick={() => { setInstallChoice(null); setPlatform(null); setBurnHost(null); }}>
            Choose again
          </Button>
        )}
      </Box>

      {installChoice === 'wipe' && burnHost && (
      <Paper id="install-steps" sx={{ p: 4, mb: 6, border: `1px solid ${alpha(theme.palette.warning.main, 0.45)}` }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
          <Usb sx={{ mr: 1 }} /> Ubuntu 24.04 with EyeSense pre-installed
        </Typography>
        <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.7 }}>
            <strong>Deer detection does not need a GPU.</strong> The included model is a small fine-tuned YOLO model.
            If you are not running a large vision model, that YOLO model fits a smaller computer, including a Raspberry Pi.
            A GPU is only for checking those detections with a large vision model, or for fine-tuning your own small model so you can later move it back to the smaller computer.
            This USB image is a separate, larger install: Ubuntu 24.04 with the desktop and hub, so plan on 16 GB of RAM and 80 GB of disk.
            With a GPU, plan on 24 GB and 128 GB.
          </Typography>
        </Alert>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>This erases two disks:</strong> the USB stick when you flash it, and the target computer&apos;s internal drive when you boot the installer. Do not use a machine you want to keep.
        </Alert>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Download />}
            href={USB_ISO_URL}
            sx={{ px: 3 }}
          >
            Download installer ISO (~9.2 GB)
          </Button>
          <Button variant="outlined" href={USB_ISO_SHA_URL}>
            SHA-256
          </Button>
          <Button variant="outlined" href={USB_CARD_URL}>
            Login card
          </Button>
        </Box>
        {burnHost === 'windows' && (
          <List dense>
            <ListItem><ListItemText primary="1. Download the ISO above (about 9.2 GB)." /></ListItem>
            <ListItem><ListItemText primary="2. Install balenaEtcher from https://etcher.balena.io and open it." /></ListItem>
            <ListItem><ListItemText primary="3. Flash → select the ISO → select your 16 GB+ USB stick → Flash. This erases the stick." /></ListItem>
            <ListItem><ListItemText primary="4. Eject the stick. Plug Ethernet into the target computer, then boot from the USB (F2 / F10 / F12 / Del — pick UEFI)." /></ListItem>
          </List>
        )}
        {burnHost === 'macos' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Easiest: install balenaEtcher from https://etcher.balena.io, then Flash → select the ISO → select the USB stick. This erases the stick.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Or from Terminal. Plug in the USB stick and list the disks. The stick is the disk labeled <code>(external, physical)</code> whose size matches the stick. The internal drive is usually <code>/dev/disk0</code>.
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2, position: 'relative' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`diskutil list`}</pre>
              <IconButton
                size="small"
                onClick={() => copyToClipboard('diskutil list', 'usb-diskutil')}
                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: alpha(theme.palette.primary.main, 0.2) }}
              >
                {copied === 'usb-diskutil' ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In this example the stick is <code>disk4</code>. Use that whole disk. A slice such as <code>disk4s1</code> is a partition on the stick. <code>disk0</code> is the Mac&apos;s own drive — leave that one alone.
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`/dev/disk0 (internal, physical):
   0:  GUID_partition_scheme  *500.3 GB  disk0

/dev/disk4 (external, physical):
   0:  FDisk_partition_scheme  *31.0 GB   disk4
   1:  DOS_FAT_32 UNTITLED     31.0 GB    disk4s1`}</pre>
            </Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              If you are unsure, unplug the stick, run <code>diskutil list</code>, plug it back in, and run it again. The new <code>(external, physical)</code> disk is the stick. Unmount that disk, then write the ISO to its raw device (<code>rdisk</code> plus the same number). This erases it.
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2, position: 'relative' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`diskutil unmountDisk /dev/disk4
sudo dd if=eyesense-usb-installer.iso of=/dev/rdisk4 bs=4m status=progress
sync`}</pre>
              <IconButton
                size="small"
                onClick={() => copyToClipboard('diskutil unmountDisk /dev/disk4\nsudo dd if=eyesense-usb-installer.iso of=/dev/rdisk4 bs=4m status=progress\nsync', 'usb-dd-mac')}
                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: alpha(theme.palette.primary.main, 0.2) }}
              >
                {copied === 'usb-dd-mac' ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Change both <code>disk4</code> and <code>rdisk4</code> to the number from your list. Run this from the folder that contains the downloaded ISO. A 9.2 GB write on USB 2.0 takes 15–25 minutes. If <code>dd</code> returns immediately with &quot;Operation not permitted&quot;, run the command in Terminal.app with Full Disk Access.
            </Typography>
          </Box>
        )}
        {burnHost === 'linux' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Plug in the USB stick, then list the disks. The stick is the new row whose <code>TRAN</code> column says <code>usb</code>. Its <code>SIZE</code> matches the stick (for example 15G, 29G, or 57G), and it is much smaller than the computer&apos;s own drive.
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2, position: 'relative' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`lsblk -o NAME,SIZE,TYPE,TRAN,MODEL`}</pre>
              <IconButton
                size="small"
                onClick={() => copyToClipboard('lsblk -o NAME,SIZE,TYPE,TRAN,MODEL', 'usb-lsblk')}
                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: alpha(theme.palette.primary.main, 0.2) }}
              >
                {copied === 'usb-lsblk' ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In this example the stick is <code>sdb</code>, so the device to write is <code>/dev/sdb</code>. Use the disk name, not a partition such as <code>sdb1</code>. The internal drive here is <code>sda</code> — leave that one alone.
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`NAME    SIZE TYPE TRAN MODEL
sda     477G disk sata Samsung SSD
sdb      29G disk usb  SanDisk
└─sdb1   29G part`}</pre>
            </Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              If you are unsure, unplug the stick, run the command, plug it back in, and run it again. The name that appears is the stick. Then write the ISO to that disk. This erases it.
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', mb: 2, position: 'relative' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`sudo dd if=eyesense-usb-installer.iso of=/dev/sdb bs=4M status=progress conv=fsync
sync`}</pre>
              <IconButton
                size="small"
                onClick={() => copyToClipboard('sudo dd if=eyesense-usb-installer.iso of=/dev/sdb bs=4M status=progress conv=fsync\nsync', 'usb-dd-linux')}
                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: alpha(theme.palette.primary.main, 0.2) }}
              >
                {copied === 'usb-dd-linux' ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Change <code>sdb</code> to the <code>NAME</code> you saw. Run this from the folder that contains the downloaded ISO.
            </Typography>
          </Box>
        )}
        <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>After the new computer boots</Typography>
        <List dense>
          <ListItem><ListItemText primary="Leave it alone. Ubuntu 24.04 and EyeSense install unattended, and the computer reboots." /></ListItem>
          <ListItem><ListItemText primary={`Log in as ${USB_FACTORY_USER} / ${USB_FACTORY_PASSWORD}. Finish the owner wizard, run setup.sh --vpn, then claim the gateway on this site.`} /></ListItem>
        </List>
        <Typography variant="caption" color="text.secondary">
          The stick never carries a device token, .env, or WireGuard keys. Those are created on the box after claim.
        </Typography>
      </Paper>
      )}

      {installChoice === 'package' && platform && (
      <Box id="install-steps" sx={{ mb: 6 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.7 }}>
            <strong>Deer detection does not need a GPU.</strong> The included model is a small fine-tuned YOLO model.
            If you are not running a large vision model, that YOLO model fits a smaller computer, including a Raspberry Pi.
            A GPU is only for checking those detections with a large vision model, or for fine-tuning your own small model so you can later move it back to the smaller computer.
            With a GPU, plan on 24 GB and 128 GB.
            The 16 GB / 80 GB figures are for the factory USB image, which also installs the desktop and hub.
          </Typography>
        </Alert>
        {deploymentSteps.find((step) => step.title.startsWith('Download'))?.content}
        <Box sx={{ mt: 4 }}>
          {deploymentSteps.find((step) => step.title.startsWith('Approve'))?.content}
        </Box>
      </Box>
      )}

      {((installChoice === 'wipe' && burnHost) || (installChoice === 'package' && platform)) && (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Alert severity="success" sx={{ mb: 3, justifyContent: 'flex-start' }}>
          {installChoice === 'wipe'
            ? 'Ubuntu 24.04 and EyeSense install from the USB image above. Booting that image erases the computer’s hard drive.'
            : 'The installer package above adds EyeSense to the operating system you already have.'}
        </Alert>
        <Button
          variant="contained"
          size="large"
          endIcon={<Download />}
          onClick={() => navigate('/register')}
          sx={{ px: 4, py: 1.5 }}
        >
          Get Started Now
        </Button>
      </Box>
      )}
    </Container>
  );
};

// Standalone wrapper with back button (for public/unauthenticated users)
const GatewayInstallationGuide = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavBar />
      
      <GatewayInstallationContent showNavBar={true} />
      <PublicFooter />
    </Box>
  );
};

export { GatewayInstallationGuide, GatewayInstallationContent };
export default GatewayInstallationGuide;
